import hash from 'object-hash';

import {
  ActiveCase,
  CaseClusterType,
  NewCase,
  NewCaseClusterType,
} from '#entities';
import {
  EClusterData,
  MAP_OVERLAY_VIEW_HEIGHT,
  MAP_OVERLAY_VIEW_OFFSET_LEFT,
  MAP_OVERLAY_VIEW_WIDTH,
  type Point,
} from '#shared';

import { getCurve } from './pathGeneration';

type CaseClusteringProps = {
  cases: (ActiveCase & {
    viewData?: {
      pointA: number[] | null;
      pointB: number[] | null;
      curve: string | null;
      organTopOffset: number;
      tripLegId?: string;
      pointerPosition?: {
        x: number;
        y: number;
        heading: number;
      };
    };
  })[];
  newCases: NewCase[];
  clientWidth: number;
  clientHeight: number;
};

const getProgressPixels = (
  curve: string,
  progress: number,
  clientWidth: number,
  clientHeight: number,
): Point | null => {
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttributeNS(null, 'd', curve);

  const pathLength = path.getTotalLength();

  const { x, y } = path.getPointAtLength(pathLength * (progress / 100));

  return [
    ((x - MAP_OVERLAY_VIEW_OFFSET_LEFT) / MAP_OVERLAY_VIEW_WIDTH) * clientWidth,
    (y / MAP_OVERLAY_VIEW_HEIGHT) * clientHeight,
  ];
};

export const getCasesPath = (
  cases: ActiveCase[],
  projection: d3.GeoProjection,
  lastUpdateTime: Date,
): ActiveCase[] => {
  const organsMap: Map<string, { items: ActiveCase[]; location: Point }> =
    new Map();
  const epoch = String(lastUpdateTime);

  // Sort by id so getCurve lane assignments are stable regardless of server response order
  return [...cases]
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .map(item => {
      const { donorHospital, recipientHospital } = item;
      if (!donorHospital?.lonLat || !recipientHospital?.lonLat) {
        return item;
      }

      let organTopOffset = 0;

      if (item.showOrgan) {
        const keyLocation = !item.mapProgress
          ? donorHospital.lonLat
          : recipientHospital.lonLat;
        const mapKey = keyLocation.toString();
        const caseList = organsMap.get(mapKey);

        if (caseList) {
          organsMap.set(mapKey, {
            items: [...caseList.items, item],
            location: caseList.location,
          });

          organTopOffset = caseList.items.length;
        } else {
          organsMap.set(mapKey, {
            items: [item],
            location: keyLocation,
          });
        }
      }

      const [xA, yA] = projection(donorHospital.lonLat) as Point;
      const [xB, yB] = projection(recipientHospital.lonLat) as Point;

      return {
        ...item,
        viewData: {
          pointA: [xA, yA],
          pointB: [xB, yB],
          curve: getCurve([xA, yA], [xB, yB], `${epoch}|${item.id}`),
          organTopOffset,
        },
      };
    });
};

export const getCaseClustering = ({
  cases,
  newCases,
  clientWidth,
  clientHeight,
}: CaseClusteringProps) => {
  const casesList = cases
    .map(item => {
      if (item.viewData?.curve) {
        const progressPixels = getProgressPixels(
          item.viewData.curve,
          item.mapProgress,
          clientWidth,
          clientHeight,
        );

        if (progressPixels) {
          return {
            type: EClusterData.Case,
            data: item,
            location: progressPixels,
          };
        }
      }

      return null;
    })
    .filter((item): item is CaseClusterType => !!item);

  const newCasesList = newCases
    .map(item => {
      if (item.donorHospital?.lonLat) {
        return {
          type: EClusterData.NewCase, // Add this to your EClusterData enum
          data: item,
          location: item.donorHospital.lonLat,
        };
      }

      return null;
    })
    .filter((item): item is NewCaseClusterType => !!item);
  const combinedList = [...casesList, ...newCasesList];

  return combinedList.map(item => ({
    key: hash(item.data.id),
    data: item.data,
    type: item.type,
    location: item.location,
  }));
};
